import type { HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { Typography } from 'components/generic/typography';
import LinkAction from 'components/shared/Routing/components/LinkAction/LinkAction';
import DiagonalLines from './DiagonalLines';
import { baseOutline, DisplayTypeSize, selectedStyling, swatchStateStyling } from './shared/styles';
import type { SwatchRendererType } from './shared/types';

type TextSwatchProps = Pick<SwatchRendererType, 'unavailable' | 'isSelected' | 'size' | 'name' | 'linkAction'> &
    HTMLAttributes<HTMLSpanElement>;

// #region Styles
const xsStyling = css`
    padding: ${({ theme }) => `${theme.Spacing4} ${theme.Spacing6}`};
    height: 24px;
`;

const smStyling = css`
    ${xsStyling}
`;

const lgStyling = css`
    height: ${DisplayTypeSize.Text.height};
    min-width: ${DisplayTypeSize.Text.width};
    padding: ${({ theme }) => `${theme.Spacing12} ${theme.Spacing8}`};
`;

const sizeStyling = {
    xs: xsStyling,
    sm: smStyling,
    lg: lgStyling
};

const baseWrapperStyles = css<{ $isSelected: boolean; $size: TextSwatchProps['size'] }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    text-align: center;
    ${baseOutline}
    ${({ $size }) => $size && sizeStyling[$size]}
    ${({ $isSelected, $size }) => $isSelected && $size && selectedStyling($size)}
    ${({ $size }) => $size && swatchStateStyling($size)}
`;

const LinkWrapper = styled(LinkAction)<{ $isSelected: boolean; $size: TextSwatchProps['size'] }>`
    ${baseWrapperStyles}
    
   
`;

const Wrapper = styled.div<{ $isSelected: boolean; $size: TextSwatchProps['size'] }>`
    ${baseWrapperStyles}
`;

const EllipsesContainer = styled(Typography.Label)`
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
`;

// #endregion Styles

export default function TextSwatch({
    name,
    isSelected,
    unavailable,
    size,
    linkAction,
    onClick,
    ...restProps
}: TextSwatchProps) {
    if (onClick) {
        return (
            <Wrapper $isSelected={isSelected} $size={size} onClick={onClick} as="button" {...restProps}>
                <EllipsesContainer size={size === 'lg' ? 'M' : 'S'}>{name}</EllipsesContainer>
                {unavailable && <DiagonalLines size={size} />}
            </Wrapper>
        );
    }

    if (!linkAction) {
        return (
            <Wrapper $isSelected={isSelected} $size={size} {...restProps}>
                <EllipsesContainer size={size === 'lg' ? 'M' : 'S'}>{name}</EllipsesContainer>
                {unavailable && <DiagonalLines size={size} />}
            </Wrapper>
        );
    }

    return (
        <LinkWrapper action={linkAction} $isSelected={isSelected} $size={size} {...restProps}>
            <EllipsesContainer size={size === 'lg' ? 'M' : 'S'}>{name}</EllipsesContainer>
            {unavailable && <DiagonalLines size={size} />}
        </LinkWrapper>
    );
}
